/*
 * Copyright (c) 2025 curryfirm.com
 */

import { createAsyncThunk, createSlice, createAction } from "@reduxjs/toolkit";
import {
    selectStartLocationDetails,
    selectEndLocationDetails,
    selectChosenDumpsterDetails,
    selectConfirmedDumpDetails,
} from "../workflow/workflowSelectors.js";
import { selectStoredRouteType } from "../workflow/workflowSlice.js";
import findOptimalRoute from '../../services/findOptimalRoute.js';
import { ROUTE_EXPIRATION_MILLISECONDS } from "../../constants.js";
import normalizeRouteResponse from "./normalizeRouteResponse.js";

export const computeRouteThunk = createAsyncThunk(
    'routing/compute',
    async (_, { getState, signal }) => {
        const { totalLength, isStale, lastComputed, moves, optimalRoute: routeApiJsonResult } = getState().routing;
        if (totalLength && !isStale && Date.now() - lastComputed < ROUTE_EXPIRATION_MILLISECONDS) {
            return {totalLength, moves, routeApiJsonResult};
        }
        const routingArgs = {
            routeType: selectStoredRouteType(getState()),
            startLoc: selectStartLocationDetails(getState()),
            endLoc: selectEndLocationDetails(getState()),
            dumpsters: selectChosenDumpsterDetails(getState()),
            dumps: selectConfirmedDumpDetails(getState()),
        };
        const result = await findOptimalRoute(routingArgs, { signal });
        return result;
    }
);

// Extra action to allow dispatchers of new routing inputs to invalidate a cached route.
export const markRouteStale = createAction("route/markStale");

const routingSlice = createSlice({
    name: 'routing',
    initialState: {
        optimalRoute: null,      // Object returned from routing service.
        routes: {},
        legs: {},
        steps: {},
        geocoding: {},
        fallbackInfo: {},
        moves: [],              // Array of { from: loc, to: loc, action: (i.e. MOVES)
        totalLength: 0,
        loading: false,
        error: null,
        lastComputed: null,
        isStale: false,
    },
    reducers: {
        clearRoute(state) {
            state.route = null;
            state.isStale = false;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(computeRouteThunk.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(computeRouteThunk.fulfilled, (state, action) => {
                state.loading = false;
                state.lastComputed = Date.now();
                state.isStale = false;

                // Transform response before storing
                const normalized = normalizeRouteResponse(action.payload);
                Object.assign(state, normalized);
            })
            .addCase(computeRouteThunk.rejected, (state, action) => {
                console.error("computeRouteThunk.rejected");
                state.loading = false;
                state.error =
                    action.payload?.message || // for thunks that use rejectWithValue
                    action.error?.message ||   // fallback for plain thrown errors
                    action.error ||            // fallback in case only a string is present
                    "Unknown error from computeRouteThunk";
            })
            .addCase(markRouteStale, (state) => {
                state.isStale = true;
            });
    }
})

export const { clearRoute } = routingSlice.actions;
export default routingSlice.reducer;

export const selectOptimalRoute = (state) => state.routing.optimalRoute;
export const selectTotalLength = (state) => state.routing.totalLength;
export const selectMoves = (state) => state.routing.moves;
export const selectRoutes = (state) => state.routing.routes;
export const selectRoutingIsLoading = (state) => state.routing.loading;
export const selectRoutingError = (state) => state.routing.error;
export const selectSteps = (state) => state.routing.steps;